import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { requestNarration } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Ctx = createContext(null);

export function useAudioPlayer() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAudioPlayer must be used within AudioPlayerProvider");
  return c;
}

function composeNarrationText(note) {
  if (!note) return "";
  const parts = [note.title];
  if (note.subtitle) parts.push(note.subtitle);
  (note.body || []).forEach((b) => {
    if (b && b.text) parts.push(b.text);
  });
  return parts.filter(Boolean).join(" ");
}

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused | error
  const [progress, setProgress] = useState({ currentTime: 0, duration: 0 });

  // Create the single, persistent HTMLAudioElement once.
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;

    const onPlay = () => setStatus("playing");
    const onPause = () => setStatus((s) => (s === "playing" ? "paused" : s));
    const onEnded = () => setStatus("idle");
    const onTime = () =>
      setProgress({ currentTime: audio.currentTime || 0, duration: audio.duration || 0 });
    const onErr = () => setStatus("error");
    const onLoaded = () =>
      setProgress({ currentTime: audio.currentTime || 0, duration: audio.duration || 0 });

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("error", onErr);

    return () => {
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("error", onErr);
      audioRef.current = null;
    };
  }, []);

  const play = useCallback(
    async (note) => {
      if (!note) return;
      const a = audioRef.current;
      if (!a) return;

      // Same note already loaded — toggle pause/resume
      if (current?.slug === note.slug && a.src) {
        if (status === "playing") {
          a.pause();
          return;
        }
        try {
          await a.play();
        } catch {
          setStatus("error");
        }
        return;
      }

      // New note — load narration
      setCurrent({ slug: note.slug, title: note.title, subtitle: note.subtitle || "" });
      setStatus("loading");
      try {
        const text = composeNarrationText(note);
        if (!text.trim()) {
          setStatus("error");
          return;
        }
        const { audio_url } = await requestNarration({
          page_id: `field-note-${note.slug}`,
          text,
        });
        a.src = `${BACKEND_URL}${audio_url}`;
        await a.play();
        setStatus("playing");
      } catch {
        setStatus("error");
      }
    },
    [current, status]
  );

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(async () => {
    try {
      await audioRef.current?.play();
    } catch {
      setStatus("error");
    }
  }, []);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    setCurrent(null);
    setStatus("idle");
    setProgress({ currentTime: 0, duration: 0 });
  }, []);

  const seek = useCallback((seconds) => {
    if (audioRef.current && !Number.isNaN(seconds)) {
      audioRef.current.currentTime = seconds;
    }
  }, []);

  const value = {
    current,
    status,
    progress,
    isActive: !!current,
    play,
    pause,
    resume,
    close,
    seek,
    audioElement: audioRef.current,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
